import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { API_ENDPOINTS } from '@common/utils/api-endpoints.const';
import {
  IDeleteAccountReason,
  IDeleteAccountReasonsResponse,
  IDeleteAccountSubmitRequest,
  IDeleteAccountSubmitResponse,
} from '@common/interfaces/delete-account/delete-account.interface';

/**
 * Session-scoped flag set right after a successful account deletion. It gates
 * the one-time DeleteAccountPending confirmation screen and is cleared when the
 * browser session ends (sessionStorage) or once the screen is shown.
 */
const DELETE_ACCOUNT_PENDING_FLAG = 'delete_account_pending';

@Injectable({
  providedIn: 'root',
})
export class DeleteAccountService {
  private readonly http = inject(HttpClient);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Reasons are static config; cached after the first fetch and reused for the session. */
  private cachedReasons: IDeleteAccountReason[] | null = null;

  getDeleteReasons(): Observable<IDeleteAccountReasonsResponse> {
    if (this.cachedReasons) return of({ reasons: this.cachedReasons });

    return this.http
      .get<IDeleteAccountReasonsResponse>(API_ENDPOINTS.DELETE_ACCOUNT_REASONS)
      .pipe(tap(res => (this.cachedReasons = res.reasons ?? [])));
  }

  submitDeleteAccount(
    payload: IDeleteAccountSubmitRequest
  ): Observable<IDeleteAccountSubmitResponse> {
    return this.http.post<IDeleteAccountSubmitResponse>(
      API_ENDPOINTS.DELETE_ACCOUNT_SUBMIT,
      payload
    );
  }

  /** Marks the account as just-deleted so the DeleteAccountPending screen can show once. */
  markDeletePending(): void {
    if (!this.isBrowser) return;
    sessionStorage.setItem(DELETE_ACCOUNT_PENDING_FLAG, 'true');
  }

  /** True when a successful deletion happened in this browser session. */
  wasDeletePending(): boolean {
    return (
      this.isBrowser &&
      sessionStorage.getItem(DELETE_ACCOUNT_PENDING_FLAG) === 'true'
    );
  }

  /** Consumes the flag so the DeleteAccountPending screen can't be revisited. */
  clearDeletePendingFlag(): void {
    if (!this.isBrowser) return;
    sessionStorage.removeItem(DELETE_ACCOUNT_PENDING_FLAG);
  }
}
